// Musical timing helpers (ported from the desktop app's `lib/timing.ts`): quantize grids, swing
// warp (mirrors `engine::schema::swing_tick`; The Infinite itself plays straight) and recorded-note
// placement.
import { PPQN } from "./clock";

const EIGHTH = PPQN / 2;
const SIXTEENTH = PPQN / 4;

export type GridDivision = "1/4" | "1/8" | "1/16" | "1/32" | "1/4T" | "1/8T" | "1/16T" | "1/32T";

export const GRID_TICKS: Record<GridDivision, number> = {
  "1/4": 960,
  "1/8": 480,
  "1/16": 240,
  "1/32": 120,
  "1/4T": 640,
  "1/8T": 320,
  "1/16T": 160,
  "1/32T": 80,
};

export const GRID_DIVISIONS = Object.keys(GRID_TICKS) as GridDivision[];

/** Swing warp: within each 1/8, the first 16th [0,240) maps to [0, 480·s) and the second to [480·s, 480). */
export function swingTick(tick: number, swingPercent: number): number {
  const s = Math.min(0.75, Math.max(0.5, swingPercent / 100));
  if (s === 0.5) return tick;
  const base = Math.floor(tick / EIGHTH) * EIGHTH;
  const f = tick - base;
  const mid = EIGHTH * s;
  const warped = f < SIXTEENTH ? f * (mid / SIXTEENTH) : mid + (f - SIXTEENTH) * ((EIGHTH - mid) / SIXTEENTH);
  return base + warped;
}

export function unswingTick(tick: number, swingPercent: number): number {
  const s = Math.min(0.75, Math.max(0.5, swingPercent / 100));
  if (s === 0.5) return tick;
  const base = Math.floor(tick / EIGHTH) * EIGHTH;
  const f = tick - base;
  const mid = EIGHTH * s;
  const straight = f < mid ? f * (SIXTEENTH / mid) : SIXTEENTH + (f - mid) * (SIXTEENTH / (EIGHTH - mid));
  return base + straight;
}

/**
 * Move `tick` toward the nearest grid line by `strength` (0–1). The result is wrapped into
 * [0, patternLength) and rounded to whole ticks.
 */
export function quantize(tick: number, gridTicks: number, strength: number, patternLength: number): number {
  const s = Math.min(1, Math.max(0, strength));
  const q = Math.round(tick / gridTicks) * gridTicks;
  let t = Math.round(tick + (q - tick) * s);
  t = ((t % patternLength) + patternLength) % patternLength;
  return t;
}

/**
 * Turn an engine `Recorded` position (audible time, swing included) into a stored note tick:
 * unswing, then quantize.
 */
export function recordedToTick(
  audibleTick: number,
  swing: number,
  gridTicks: number,
  strength: number,
  patternLength: number,
): number {
  const straight = unswingTick(((audibleTick % patternLength) + patternLength) % patternLength, swing);
  return quantize(straight, gridTicks, strength, patternLength);
}

/** Signed distance a→b on a loop of length `len`, in (−len/2, len/2]. */
export function loopDelta(a: number, b: number, len: number): number {
  let d = (((b - a) % len) + len) % len;
  if (d > len / 2) d -= len;
  return d;
}
